import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, ShieldCheck, Clock, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "Delete my ID documents" — self-service destruction of the KYC files.
// Backend decides eligibility (30-day hold after the last money event);
// the verified status is never touched, only the documents are shredded.
const IDDocumentsPanel = ({ status, token, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const holdDays = status?.id_hold_days || 30;

  const destroy = async () => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/me/id-vault/documents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(data?.message || "Your ID documents have been deleted.");
      setOpen(false);
      onChanged?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Couldn't delete your documents right now"));
      if (d?.blockers) onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-[#1A2030] bg-[#0A0E18] p-5" data-testid="id-documents-panel">
      <h2 className="font-bold text-white text-lg mb-1 flex items-center gap-2">
        <Trash2 className="w-4 h-4 text-[#EC008C]" /> Your ID documents
      </h2>

      {status?.documents_redacted ? (
        <p className="text-sm text-[#8DB87A] flex items-start gap-2 mt-2" data-testid="id-documents-redacted">
          <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
          Your ID documents have been deleted{status.redacted_at ? ` on ${String(status.redacted_at).slice(0, 10)}` : ""}. Your verified status is unchanged.
        </p>
      ) : status?.can_redact ? (
        <>
          <p className="text-sm text-[#8A92A0] mt-1">
            You can permanently destroy the ID and selfie files in our compliance vault. Your approved status stays exactly as it is.
          </p>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="mt-4 h-10 px-4 rounded-lg border border-[#EC008C]/50 text-[#EC008C] text-sm font-semibold hover:bg-[#EC008C]/10 inline-flex items-center gap-2"
            data-testid="delete-id-documents-btn"
          >
            <Trash2 className="w-4 h-4" /> Delete my ID documents
          </button>
        </>
      ) : (
        <div className="mt-1" data-testid="id-documents-hold">
          <p className="text-sm text-[#8A92A0] flex items-start gap-2">
            <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#F0C464]" />
            Available {holdDays} days after your last purchase, subscription or event.
          </p>
          {status?.redact_blockers?.length > 0 && (
            <ul className="mt-2 ml-6 space-y-1 text-xs text-[#6A7280]" data-testid="id-documents-blockers">
              {status.redact_blockers.map((b) => <li key={b}>• {b}</li>)}
            </ul>
          )}
        </div>
      )}

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="bg-[#0A0E18] border-[#1A2030] text-white" data-testid="delete-id-documents-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your ID documents?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#8A92A0]">
              The ID and selfie files will be permanently destroyed in our compliance vault and cannot be recovered.
              Your verified status is unchanged. If we ever need to re-verify you, you&apos;ll simply be asked to upload again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#1A2030] text-white hover:bg-[#1A2030]" data-testid="delete-id-documents-cancel">Keep them</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); destroy(); }}
              disabled={busy}
              className="bg-[#EC008C] text-black hover:bg-[#FF3399]"
              data-testid="delete-id-documents-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default IDDocumentsPanel;
